"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Award, Trophy, Star, Medal, ChevronRight, Calendar } from "lucide-react"
import SEO from "../SEO"


export default function AwardsPage() {
  const awardsSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad recognized with numerous awards for excellence in healthcare and patient safety.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "telephone": "+91-99660-60000",
      "availableLanguage": ["English", "Telugu", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/about/awards"
    },
    "headline": "Awards & Recognitions of Pranaam Hospitals",
    "description": "Pranaam Hospitals has received multiple awards and recognitions for clinical excellence, patient safety, and quality healthcare services in Hyderabad.",
    "image": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/hospital-awards.jpg",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals awards",
      "best hospital awards Hyderabad",
      "NABH accredited hospital",
      "healthcare excellence awards Telangana"
    ]
  }
];

  const [selectedCategory, setSelectedCategory] = useState("all")

  const categories = [
    { id: "all", name: "All Awards" },
    { id: "national", name: "National Rankings" },
    { id: "accreditation", name: "Accreditations" },
    { id: "specialties", name: "Specialty Awards" },
    { id: "innovation", name: "Innovation" },
  ]

  const awards = [
    {
      year: "2023",
      category: "innovation",
      title: "India's First Health ATM",
      description: "Launched India's first Health ATM, revolutionizing healthcare accessibility",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "national",
      title: "11th Best National Multispecialty in Paediatrics",
      description: "Recognized nationally for excellence in pediatric care",
      icon: "medal",
    },
    {
      year: "2023",
      category: "national",
      title: "11th Best National Multispecialty in Gynaecology",
      description: "National recognition for outstanding gynecological services",
      icon: "medal",
    },
    {
      year: "2023",
      category: "specialties",
      title: "5th Best South Multispecialty in Paediatrics",
      description: "Top 5 pediatric hospital in South India",
      icon: "star",
    },
    {
      year: "2023",
      category: "specialties",
      title: "4th Best South Multispecialty in Gynaecology",
      description: "Leading gynecology hospital in South India",
      icon: "star",
    },
    {
      year: "2023",
      category: "specialties",
      title: "2nd Best Hyderabad Multispecialty in Paediatrics",
      description: "Second-best pediatric hospital in Hyderabad",
      icon: "award",
    },
    {
      year: "2023",
      category: "specialties",
      title: "2nd Best Hyderabad Multispecialty in Gynaecology",
      description: "Top-ranked gynecology hospital in Hyderabad",
      icon: "award",
    },
    {
      year: "2023",
      category: "specialties",
      title: "6th Best Hyderabad Multispecialty in Emergency and Trauma",
      description: "Excellence in emergency and trauma care",
      icon: "award",
    },
    {
      year: "2023",
      category: "national",
      title: "Asia's Leading Hospitals by CMO Asia",
      description: "Recognized as one of Asia's leading healthcare providers",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "national",
      title: "National Awards for Excellence - Best Customer Service",
      description: "Outstanding customer service in healthcare",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "specialties",
      title: "19th Best South Multispecialty in Gastroenterology",
      description: "Regional recognition for gastroenterology services",
      icon: "star",
    },
    {
      year: "2023",
      category: "national",
      title: "National Awards for Excellence - Best Multispeciality Hospital",
      description: "National excellence in multispecialty healthcare",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "national",
      title: "Berkshire Corporate Awards - India's Best Brand of the Year",
      description: "Recognized as India's best healthcare brand",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "national",
      title: "Business Icon Award by Outlook",
      description: "Business excellence in healthcare sector",
      icon: "trophy",
    },
    {
      year: "2023",
      category: "specialties",
      title: "6th Best Hyderabad Multispecialty in Gastroenterology",
      description: "Top gastroenterology hospital in Hyderabad",
      icon: "award",
    },
    {
      year: "2021",
      category: "national",
      title: "6th Best Hospital in South India",
      description: "Times of India Survey recognition",
      icon: "medal",
    },
    {
      year: "2021",
      category: "specialties",
      title: "3rd Best Paediatric Hospital in Hyderabad",
      description: "Times of India Survey - Hyderabad Multispecialty",
      icon: "award",
    },
    {
      year: "2021",
      category: "specialties",
      title: "2nd Best Gynaecology and Obstetrics Hospital",
      description: "Times of India Survey - Hyderabad Multispecialty",
      icon: "award",
    },
    {
      year: "2021",
      category: "accreditation",
      title: "NABL Accreditation",
      description: "National Accreditation Board for Laboratories",
      icon: "medal",
    },
    {
      year: "2020",
      category: "accreditation",
      title: "NABH Re-Accreditation",
      description: "National Accreditation Board for Hospitals - Re-accredited",
      icon: "medal",
    },
    {
      year: "2017",
      category: "national",
      title: "4th Best Hospital",
      description: "Times of India Survey recognition",
      icon: "medal",
    },
    {
      year: "2016",
      category: "national",
      title: "4th Best Hospital",
      description: "Times of India Survey recognition",
      icon: "medal",
    },
    {
      year: "2015",
      category: "accreditation",
      title: "NABH Accreditation",
      description: "National Accreditation Board for Hospitals and Healthcare Providers",
      icon: "medal",
    },
    {
      year: "2015",
      category: "national",
      title: "6th Best Hospital",
      description: "Times of India Survey recognition",
      icon: "medal",
    },
    {
      year: "",
      category: "specialties",
      title: "Health Excellence Award - Best in Gynaecology Care",
      description: "Times of India Survey recognition",
      icon: "trophy",
    },
  ]

  const filteredAwards =
    selectedCategory === "all" ? awards : awards.filter((award) => award.category === selectedCategory)

 const getIconComponent = (iconName) => {
  switch (iconName) {
    case "trophy":
      return Trophy
    case "medal":
      return Medal
    case "star":
      return Star
    default:
      return Award
  }
}

  return (
    <>
    <SEO
  title="Awards & Recognitions | Pranaam Hospitals Hyderabad"
  description="Explore the awards and recognitions earned by Pranaam Hospitals for excellence in healthcare, patient safety, and medical innovation in Hyderabad."
  canonical="https://pranaam.pranaamhospitals.in/about/awards"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals awards, hospital awards Hyderabad, NABH accredited hospital Hyderabad,
            best hospital awards Telangana, healthcare excellence awards Hyderabad,
            Pranaam Hospitals recognitions, multispecialty hospital awards"
  schema={awardsSchema}
/>
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-primary/5 py-16 md:py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Recognition & Excellence</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Awards & <span className="text-primary">Achievements</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Although hitting milestones isn't our ultimate goal, we're humbled by the praise and accolades awarded
                to us by virtue of our passion, zeal and excellence in medicine serving countless patients to help them
                live their best lives.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <Badge variant="outline" className="px-4 py-2 text-base">
                  <Trophy className="w-4 h-4 mr-2" />
                  50+ Awards
                </Badge>
                <Badge variant="outline" className="px-4 py-2 text-base">
                  <Medal className="w-4 h-4 mr-2" />
                  NABH & NABL Certified
                </Badge>
                <Badge variant="outline" className="px-4 py-2 text-base">
                  <Star className="w-4 h-4 mr-2" />
                  National Recognition
                </Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Trophy className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-primary mb-2">50+</h3>
                <p className="text-gray-600">Total Awards</p>
              </div>
              <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-primary mb-2">15+</h3>
                <p className="text-gray-600">National Rankings</p>
              </div>
              <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Medal className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-primary mb-2">4</h3>
                <p className="text-gray-600">Major Certifications</p>
              </div>
              <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Star className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-primary mb-2">10+</h3>
                <p className="text-gray-600">Years of Recognition</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category.id)}
                    className="rounded-full"
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Awards Grid */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAwards.map((award, index) => {
                  const IconComponent = getIconComponent(award.icon)
                  return (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary group-hover:text-white transition-colors">
                            <IconComponent className="w-6 h-6 text-primary group-hover:text-white" />
                          </div>
                        </div>
                        <div className="flex-1"> 
                          {award.year && (
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="w-4 h-4 text-primary" />
                              <span className="text-sm font-medium text-primary">{award.year}</span>
                            </div>
                          )}
                          <h3 className="font-bold mb-2 text-gray-900 group-hover:text-primary transition-colors">
                            {award.title}
                          </h3>
                          <p className="text-sm text-gray-600">{award.description}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Award Gallery Section */}
        {/* <section className="py-12 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-2">Gallery</Badge>
                <h2 className="mb-4 text-3xl font-bold md:text-4xl">Our Proud Moments</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Visual documentation of our achievements and recognition over the years
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  "besthospital-pranaam",
                  "pranaam-awards",
                  "pranaam",
                  "pranaamhospitals",
                  "pranaamhospital-awards",
                  "pranaam-hospital",
                  "pranaamhospitalinhyderabad",
                  "pranaam-madinaguda",
                  "pranaam-hospital-isnapur",
                  "pranaamhoapital-zaheerabad",
                  "pranaamhospital-nallgandla",
                ].map((imageName, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer">
                    <img
                      src={`/placeholder_image.png?height=300&width=300&text=${imageName}`}
                      alt={`Award ${index + 1}`}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <Award className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section> */}

        {/* Major Accreditations */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-2">Certifications</Badge>
                <h2 className="mb-4 text-3xl font-bold md:text-4xl">Major Accreditations</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Quality certifications that validate our commitment to excellence
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-lg p-8 border-2 border-primary/20">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Medal className="w-8 h-8 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">NABH Accreditation</h3>
                      <p className="text-gray-600 mb-3">
                        National Accreditation Board for Hospitals and Healthcare Providers
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-primary">Initial:</span>
                          <span className="text-gray-600">November 2015</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-primary">Re-accredited:</span>
                          <span className="text-gray-600">January 2020</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-8 border-2 border-primary/20">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Medal className="w-8 h-8 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">NABL Accreditation</h3>
                      <p className="text-gray-600 mb-3">National Accreditation Board for Laboratories</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-primary">Certified:</span>
                          <span className="text-gray-600">January 2021</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary/90 rounded-lg shadow-xl p-8 md:p-12 text-white text-center">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Experience Award-Winning Healthcare</h2>
              <p className="mb-8 text-lg opacity-90">
                Join thousands of patients who have trusted our nationally recognized healthcare services.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" variant="secondary" asChild>
                  <Link href="/book-appointment">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book an Appointment
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-primary"
                  asChild
                >
                  <Link href="/about">
                    Learn More About Us
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}
